import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import Button from '../../../shared/components/Button';
import {
  usePackageTemplates,
  useCreatePackageTemplate,
  useUpdatePackageTemplate,
  useDeletePackageTemplate,
  useAddOns,
  useCreateAddOn,
  useUpdateAddOn,
  useRemoveAddOn,
} from '../hooks/usePackages';
import { PackageTemplateModal } from '../components/PackageTemplateModal';
import { AddOnModal } from '../components/AddOnModal';
import { BranchAvailabilityEditor } from '../../branches/components/BranchAvailabilityEditor';
import { Modal } from '../../../shared/components/Modal';
import type {
  PackageTemplate,
  AddOn,
  CreatePackageTemplatePayload,
  CreateAddOnPayload,
} from '../api/packagesApi';

const naira = (kobo: number) => `₦${((kobo ?? 0) / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function PackagesPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.BOOKING_SERVICE_MANAGE);

  const { data: templates = [], isLoading: templatesLoading } = usePackageTemplates(selectedTenantId);
  const { data: addons = [], isLoading: addonsLoading } = useAddOns(selectedTenantId);

  const createTemplate = useCreatePackageTemplate();
  const updateTemplate = useUpdatePackageTemplate();
  const deleteTemplate = useDeletePackageTemplate();
  const createAddOn = useCreateAddOn();
  const updateAddOn = useUpdateAddOn();
  const removeAddOn = useRemoveAddOn();

  const [templateModal, setTemplateModal] = useState<{ open: boolean; editing: PackageTemplate | null }>({ open: false, editing: null });
  const [addonModal, setAddonModal] = useState<{ open: boolean; editing: AddOn | null }>({ open: false, editing: null });
  // Per-branch availability for one package listing (PE-2139).
  const [branchesTemplate, setBranchesTemplate] = useState<PackageTemplate | null>(null);

  const templateName = (id: string) => templates.find((t) => t.id === id)?.name ?? id;

  const handleTemplateSubmit = async (values: CreatePackageTemplatePayload) => {
    if (templateModal.editing) await updateTemplate.mutateAsync({ id: templateModal.editing.id, data: values });
    else await createTemplate.mutateAsync(values);
    setTemplateModal({ open: false, editing: null });
  };
  const handleAddOnSubmit = async (values: CreateAddOnPayload) => {
    if (addonModal.editing) await updateAddOn.mutateAsync({ id: addonModal.editing.id, data: values });
    else await createAddOn.mutateAsync(values);
    setAddonModal({ open: false, editing: null });
  };

  if (!selectedTenantId) {
    return (
      <div className="py-6 max-w-6xl mx-auto px-2">
        <h1 className="text-[23px] font-bold text-foreground mb-6">Packages &amp; Add-ons</h1>
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          Select a merchant to manage their packages and add-ons.
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="mb-8">
        <h1 className="text-[23px] font-bold text-foreground">Packages &amp; Add-ons</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Configure reusable package templates and the optional add-ons customers can attach.
        </p>
      </div>

      {/* Templates */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[16px] font-semibold text-foreground">Package templates</h2>
          {canManage && (
            <Button size="sm" className="h-[40px]" onClick={() => setTemplateModal({ open: true, editing: null })}>
              New template
            </Button>
          )}
        </div>
        {templatesLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading templates…</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
            No package templates yet.{canManage && ' Click "New template" to create one.'}
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Steps</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Price</th>
                  {canManage && <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-muted transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{t.name}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{t.steps?.length ?? 0} step{(t.steps?.length ?? 0) === 1 ? '' : 's'}</td>
                    <td className="px-6 py-4 text-sm text-foreground">{naira(t.price)}</td>
                    {canManage && (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <button onClick={() => setTemplateModal({ open: true, editing: t })} className="text-xs text-blue-600 hover:underline">Edit</button>
                          <button onClick={() => setBranchesTemplate(t)} className="text-xs text-blue-600 hover:underline">Branches</button>
                          <button onClick={() => deleteTemplate.mutate(t.id)} disabled={deleteTemplate.isPending} className="text-xs text-red-600 hover:underline disabled:opacity-50">Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Add-ons */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[16px] font-semibold text-foreground">Add-ons</h2>
          {canManage && (
            <Button size="sm" className="h-[40px]" onClick={() => setAddonModal({ open: true, editing: null })}>
              New add-on
            </Button>
          )}
        </div>
        {addonsLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading add-ons…</div>
        ) : addons.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
            No add-ons yet.{canManage && ' Click "New add-on" to create one.'}
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Price</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Extra time</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Attached to</th>
                  {canManage && <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {addons.map((a) => {
                  const merchantOwned = a.source === 'MERCHANT';
                  return (
                    <tr key={a.id} className="hover:bg-muted transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">{a.name}</td>
                      <td className="px-6 py-4 text-sm text-foreground">{naira(a.price)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{a.extraDurationMinutes ? `${a.extraDurationMinutes} min` : '—'}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {a.packageIds?.length ? a.packageIds.map(templateName).join(', ') : '—'}
                      </td>
                      {canManage && (
                        <td className="px-6 py-4">
                          {merchantOwned ? (
                            <div className="flex items-center gap-4">
                              <button onClick={() => setAddonModal({ open: true, editing: a })} className="text-xs text-blue-600 hover:underline">Edit</button>
                              <button onClick={() => removeAddOn.mutate(a.id)} disabled={removeAddOn.isPending} className="text-xs text-red-600 hover:underline disabled:opacity-50">Archive</button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">{a.source}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <PackageTemplateModal
        isOpen={templateModal.open}
        editing={templateModal.editing}
        onClose={() => setTemplateModal({ open: false, editing: null })}
        onSubmit={handleTemplateSubmit}
        isSaving={createTemplate.isPending || updateTemplate.isPending}
      />
      <AddOnModal
        isOpen={addonModal.open}
        editing={addonModal.editing}
        templates={templates}
        onClose={() => setAddonModal({ open: false, editing: null })}
        onSubmit={handleAddOnSubmit}
        isSaving={createAddOn.isPending || updateAddOn.isPending}
      />

      <Modal isOpen={!!branchesTemplate} onClose={() => setBranchesTemplate(null)} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              Branch availability{branchesTemplate ? ` — ${branchesTemplate.name}` : ''}
            </h2>
            <button
              onClick={() => setBranchesTemplate(null)}
              className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none"
            >
              &times;
            </button>
          </div>
          <BranchAvailabilityEditor
            entity="packageListing"
            entityId={branchesTemplate?.id}
            merchantId={selectedTenantId}
            disabled={!canManage}
          />
        </div>
      </Modal>
    </div>
  );
}
